import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DownloadLink, FileUpload } from "@/components/FileUpload";
import { useToast } from "@/components/Toast";
import { Badge, Button, Card, Empty, ErrorNote, Field, inputClass, Spinner } from "@/components/ui";
import {
  api,
  type ChecklistItemRow,
  type DealDetail as DealDetailData,
  type DealStage,
  type EngagementRow,
  type EngagementStatus,
} from "@/lib/api";
import {
  CHECKLIST_CATEGORY_LABEL,
  DEAL_STAGE_LABEL,
  dealStageTone,
  ENGAGEMENT_SERVICES,
  ENGAGEMENT_STATUS_LABEL,
  humanise,
  STRATEGY_LABEL,
} from "@/lib/dealStages";
import { formatAcres, formatDate, formatDateTime, formatDue, formatMoneyCents, formatPct } from "@/lib/format";
import { cn } from "@/lib/utils";

const TABS = [
  "Checklist",
  "Engagements",
  "Funding",
  "Underwriting",
  "Disposition",
  "Documents",
  "Timeline",
] as const;
type Tab = (typeof TABS)[number];

const STATUS_TONE: Record<string, "ok" | "info" | "warn" | "danger" | "muted"> = {
  done: "ok",
  in_progress: "info",
  todo: "warn",
  na: "muted",
  kill: "danger",
};

function DiligenceFindings({ deal }: { deal: DealDetailData }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [flood, setFlood] = useState("");
  const [access, setAccess] = useState("");
  const [frontage, setFrontage] = useState("");

  const { data: lead } = useQuery({
    queryKey: ["lead", deal.deal.lead_id],
    queryFn: () => api.lead(deal.deal.lead_id),
  });
  const parcelId = lead?.parcels[0]?.id;

  const save = useMutation({
    // Send only what was filled in. An untouched field must not be transmitted as null, or the
    // server cannot tell "clear this" from "I did not type here".
    mutationFn: () => {
      const patch: Parameters<typeof api.patchParcel>[1] = {};
      if (flood !== "") patch.floodplain_pct = flood;
      if (access !== "") patch.legal_access = access as "unknown" | "yes" | "no" | "easement";
      if (frontage !== "") patch.road_frontage_ft = Number(frontage);
      return api.patchParcel(parcelId!, patch);
    },
    onSuccess: () => {
      toast.push("Diligence findings recorded");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  if (!parcelId) return null;

  return (
    <div
      className="mb-3 flex flex-wrap items-end gap-2 rounded border bg-surface-2 p-2"
      data-testid="diligence-findings"
    >
      <Field label="FEMA SFHA %">
        <input
          value={flood}
          onChange={(e) => setFlood(e.target.value)}
          inputMode="decimal"
          placeholder={String(lead?.parcels[0]?.floodplain_pct ?? "—")}
          className={inputClass + " h-7 w-24 py-0 text-xs tabular-nums"}
          data-testid="finding-floodplain"
        />
      </Field>
      <Field label="Legal access">
        <select
          value={access}
          onChange={(e) => setAccess(e.target.value)}
          className={inputClass + " h-7 w-28 py-0 text-xs"}
          data-testid="finding-access"
        >
          <option value="">{lead?.parcels[0]?.legal_access ?? "unchanged"}</option>
          {["unknown", "yes", "no", "easement"].map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Road frontage ft">
        <input
          value={frontage}
          onChange={(e) => setFrontage(e.target.value)}
          inputMode="numeric"
          placeholder={String(lead?.parcels[0]?.road_frontage_ft ?? "—")}
          className={inputClass + " h-7 w-24 py-0 text-xs tabular-nums"}
          data-testid="finding-frontage"
        />
      </Field>
      <Button
        size="sm"
        variant="primary"
        disabled={save.isPending || (!flood && !access && !frontage)}
        onClick={() => save.mutate()}
        data-testid="finding-save"
      >
        Record
      </Button>
      <span className="text-[11px] text-fg-muted">
        These feed the §6.5 kill checks above.
      </span>
    </div>
  );
}

function ChecklistRow({ item }: { item: ChecklistItemRow }) {
  const toast = useToast();
  const qc = useQueryClient();
  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.patchChecklistItem>[1]) =>
      api.patchChecklistItem(item.id, body),
    onSuccess: () => {
      toast.push(`${item.label} updated`);
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  return (
    <tr className="border-b last:border-b-0" data-testid="checklist-row">
      <td className="px-2 py-1.5 text-xs">
        <span className={cn(!item.required && "text-fg-muted")}>{item.label}</span>
        {!item.required && <span className="ml-1 text-[10px] text-fg-muted">(optional)</span>}
      </td>
      <td className="px-2 py-1.5">
        <select
          value={item.status}
          onChange={(e) =>
            patch.mutate({ status: e.target.value as ChecklistItemRow["status"] })
          }
          aria-label={`Status for ${item.label}`}
          className={inputClass + " h-6 w-28 py-0 text-xs"}
        >
          {(["todo", "in_progress", "done", "na", "kill"] as const).map((s) => (
            <option key={s} value={s}>
              {humanise(s)}
            </option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1.5">
        <Badge tone={STATUS_TONE[item.status] ?? "muted"}>{humanise(item.status)}</Badge>
      </td>
      <td className="px-2 py-1.5 text-[11px] text-fg-muted">
        {item.verified_by_email ? `${item.verified_by_email} · ${formatDate(item.verified_at)}` : "—"}
      </td>
      <td className="px-2 py-1.5">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => patch.mutate({ verified: !item.verified_by_email })}
        >
          {item.verified_by_email ? "Unverify" : "Verify"}
        </Button>
      </td>
    </tr>
  );
}

function EngagementCard({ engagement }: { engagement: EngagementRow }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(engagement.rfq_draft ?? "");
  const [notes, setNotes] = useState(engagement.notes ?? "");

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.patchEngagement>[1]) =>
      api.patchEngagement(engagement.id, body),
    onSuccess: (row) => {
      toast.push(`${humanise(row.service)} → ${ENGAGEMENT_STATUS_LABEL[row.status]}`);
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  const mailto = `mailto:${engagement.vendor_email ?? ""}?subject=${encodeURIComponent(
    `${humanise(engagement.service)} — ${engagement.deal_name ?? "our tract"}`,
  )}&body=${encodeURIComponent(draft)}`;

  return (
    <div className="rounded border p-2" data-testid={`engagement-${engagement.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{humanise(engagement.service)}</span>
        <Badge tone={engagement.overdue ? "danger" : "muted"}>
          {ENGAGEMENT_STATUS_LABEL[engagement.status]}
        </Badge>
        {engagement.overdue && <Badge tone="danger">overdue</Badge>}
        <span className="text-xs text-fg-muted">
          {engagement.vendor_name}
          {engagement.vendor_company ? ` · ${engagement.vendor_company}` : ""}
        </span>
        {engagement.due_at && (
          <span className="ml-auto text-xs tabular-nums text-fg-muted">
            due {formatDue(engagement.due_at)}
          </span>
        )}
      </div>

      <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] sm:grid-cols-4">
        <dt className="text-fg-muted">Quote</dt>
        <dd className="tabular-nums">{formatMoneyCents(engagement.quote_cents)}</dd>
        <dt className="text-fg-muted">Agreed</dt>
        <dd className="tabular-nums">{formatMoneyCents(engagement.agreed_cents)}</dd>
        <dt className="text-fg-muted">Paid</dt>
        <dd className="tabular-nums">{formatMoneyCents(engagement.paid_cents)}</dd>
        <dt className="text-fg-muted">Deliverables</dt>
        <dd className="tabular-nums">{engagement.deliverable_count}</dd>
      </dl>

      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-fg-muted">
          RFQ draft {engagement.rfq_draft_source === "user" ? "(edited)" : ""}
        </summary>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={10}
          className={inputClass + " mt-1 font-mono text-[11px]"}
          data-testid="rfq-draft"
        />
        <div className="mt-1 flex flex-wrap gap-1">
          <Button size="sm" onClick={() => patch.mutate({ rfq_draft: draft })}>
            Save draft
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void navigator.clipboard.writeText(draft)}
          >
            Copy
          </Button>
          <a
            href={mailto}
            className="inline-flex h-6 items-center rounded border border-transparent px-2 text-xs text-fg-muted hover:bg-surface-2 hover:text-fg"
          >
            Open mail draft
          </a>
          <span className="self-center text-[10px] text-fg-muted">
            This system never sends it — you do.
          </span>
        </div>
      </details>

      <div className="mt-2 flex flex-wrap items-end gap-2">
        {engagement.allowed_statuses.length > 0 && (
          <Field label="Move to">
            <select
              defaultValue=""
              onChange={(e) => {
                const next = e.target.value;
                if (next) {
                  patch.mutate({ status: next as EngagementStatus, notes: notes || null });
                }
              }}
              className={inputClass + " h-7 w-40 py-0 text-xs"}
              data-testid="engagement-status-select"
            >
              <option value="">—</option>
              {engagement.allowed_statuses.map((s) => (
                <option key={s} value={s}>
                  {ENGAGEMENT_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </Field>
        )}
        <div className="min-w-48 flex-1">
          <Field label="Note (counts as delivery evidence)">
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputClass + " h-7 py-0 text-xs"}
              data-testid="engagement-notes"
            />
          </Field>
        </div>
        <FileUpload
          entityType="engagement"
          entityId={engagement.id}
          kind="other"
          label="Upload deliverable"
          testId="upload-deliverable"
          onUploaded={async (doc) => {
            await api.attachDeliverable(engagement.id, doc.id);
          }}
        />
      </div>
    </div>
  );
}

function AddEngagement({ deal }: { deal: DealDetailData }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [service, setService] = useState<string>("boundary_survey");
  const [vendorId, setVendorId] = useState("");
  const [due, setDue] = useState("");

  // Every active vendor, with the ones covering this parcel's state first. Filtering strictly
  // by state produced an empty dropdown and no explanation whenever nobody covered the state —
  // a dead end rather than a filter. Out-of-state vendors are still offered, and labelled.
  const { data: allVendors } = useQuery({
    queryKey: ["vendors", "active"],
    queryFn: () => api.vendors({ active: true }),
  });
  const state = deal.deal.parcel_state;
  const vendors = (allVendors ?? [])
    .map((v) => ({ vendor: v, covers: state ? v.states.includes(state) : true }))
    .sort((a, b) => Number(b.covers) - Number(a.covers) || a.vendor.name.localeCompare(b.vendor.name));
  const anyInState = vendors.some((v) => v.covers);

  const create = useMutation({
    mutationFn: () =>
      api.createEngagement({
        vendor_id: vendorId,
        service,
        deal_id: deal.deal.id,
        lead_id: null,
        parcel_id: null,
        due_at: due ? new Date(due).toISOString() : null,
        notes: null,
      }),
    onSuccess: () => {
      toast.push("Engagement created with an RFQ draft");
      setVendorId("");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  return (
    <form
      className="mb-3 flex flex-wrap items-end gap-2 rounded border bg-surface-2 p-2"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <Field label="Service">
        <select
          value={service}
          onChange={(e) => setService(e.target.value)}
          className={inputClass + " w-44"}
          data-testid="new-engagement-service"
        >
          {ENGAGEMENT_SERVICES.map((s) => (
            <option key={s} value={s}>
              {humanise(s)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Vendor">
        <select
          value={vendorId}
          onChange={(e) => setVendorId(e.target.value)}
          className={inputClass + " w-56"}
          data-testid="new-engagement-vendor"
        >
          <option value="">Choose a vendor…</option>
          {vendors.map(({ vendor: v, covers }) => (
            <option key={v.id} value={v.id}>
              {v.name} — {humanise(v.type)}
              {v.rating ? ` (${v.rating}★)` : ""}
              {state && !covers ? ` · outside ${state}` : ""}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Due">
        <input
          type="datetime-local"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          className={inputClass + " w-52"}
          data-testid="new-engagement-due"
        />
      </Field>
      <Button
        type="submit"
        variant="primary"
        disabled={!vendorId || create.isPending}
        data-testid="new-engagement-create"
      >
        Add engagement
      </Button>
      {state && !anyInState && vendors.length > 0 && (
        <span className="text-[11px] text-warn" data-testid="no-in-state-vendor">
          No vendor on file covers {state} — the list below is everyone else.
        </span>
      )}
    </form>
  );
}

function UnderwritingTab({ deal }: { deal: DealDetailData }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [inputs, setInputs] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(deal.underwriting_inputs).map(([k, v]) => [
        k,
        typeof v === "number" || typeof v === "string" ? String(v) : "",
      ]),
    ),
  );

  const FIELDS: { key: string; label: string; money?: boolean }[] = [
    { key: "purchase_price", label: "Purchase price", money: true },
    { key: "closing_costs", label: "Closing costs", money: true },
    { key: "survey", label: "Survey", money: true },
    { key: "soils", label: "Soils", money: true },
    { key: "engineering", label: "Engineering", money: true },
    { key: "legal", label: "Legal", money: true },
    { key: "road_utilities", label: "Road & utilities", money: true },
    { key: "holding_months", label: "Holding months" },
    { key: "monthly_carry", label: "Monthly carry", money: true },
    { key: "commission_pct", label: "Commission %" },
    { key: "projected_lots", label: "Projected lots" },
    { key: "price_per_lot", label: "Price per lot", money: true },
    { key: "other_exit_value", label: "Other exit value", money: true },
  ];

  const save = useMutation({
    mutationFn: () =>
      api.patchDeal(deal.deal.id, {
        underwriting: Object.fromEntries(
          Object.entries(inputs).map(([k, v]) => [k, v === "" ? 0 : Number(v)]),
        ),
      }),
    onSuccess: () => {
      toast.push("Underwriting saved");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  const u = deal.underwriting;
  const derived: { label: string; value: string; strong?: boolean }[] = [
    { label: "Projected exit", value: formatMoneyCents(u.projected_exit_cents) },
    { label: "Commission", value: formatMoneyCents(u.commission_cents) },
    { label: "Carry", value: formatMoneyCents(u.carry_cents) },
    { label: "Hard costs", value: formatMoneyCents(u.hard_costs_cents) },
    { label: "All-in cost", value: formatMoneyCents(u.all_in_cost_cents) },
    { label: "Projected spread", value: formatMoneyCents(u.projected_spread_cents), strong: true },
    { label: "Margin", value: u.margin_pct === null ? "—" : `${u.margin_pct}%` },
    { label: "Spread per acre", value: formatMoneyCents(u.spread_per_acre_cents) },
    { label: "Breakeven per lot", value: formatMoneyCents(u.breakeven_price_per_lot_cents) },
    { label: "Sunk DD cost (paid)", value: formatMoneyCents(u.sunk_dd_cost_cents) },
    { label: "Expected value", value: formatMoneyCents(u.expected_value_cents), strong: true },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <Card title="Inputs (cents)">
        <div className="grid grid-cols-2 gap-2">
          {FIELDS.map((f) => (
            <Field key={f.key} label={f.label}>
              <input
                value={inputs[f.key] ?? ""}
                onChange={(e) => setInputs({ ...inputs, [f.key]: e.target.value })}
                inputMode="numeric"
                className={inputClass + " h-7 py-0 text-xs tabular-nums"}
                data-testid={`uw-${f.key}`}
              />
            </Field>
          ))}
        </div>
        <Button
          className="mt-3 w-full"
          variant="primary"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          data-testid="uw-save"
        >
          {save.isPending ? "Saving…" : "Save and recompute"}
        </Button>
        <p className="mt-2 text-[11px] text-fg-muted">
          Every amount is integer cents. The maths runs in Python (§6.7); nothing here is
          estimated by a model.
        </p>
      </Card>
      <Card title="Derived" data-testid="uw-derived">
        <dl className="space-y-1 text-xs">
          {derived.map((d) => (
            <div key={d.label} className="flex justify-between gap-2">
              <dt className="text-fg-muted">{d.label}</dt>
              <dd className={cn("tabular-nums", d.strong && "font-semibold")}>{d.value}</dd>
            </div>
          ))}
        </dl>
        {u.warnings.length > 0 && (
          <ul className="mt-2 space-y-0.5 text-[11px] text-warn">
            {u.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
        {deal.anchor_low_cents !== null && (
          <p className="mt-2 text-[11px] text-fg-muted">
            Anchor band from CAD land value: {formatMoneyCents(deal.anchor_low_cents)} –{" "}
            {formatMoneyCents(deal.anchor_high_cents)}
          </p>
        )}
      </Card>
    </div>
  );
}

/** §7.5 deal detail. */
export function DealDetail() {
  const { dealId } = useParams<{ dealId: string }>();
  const [tab, setTab] = useState<Tab>("Checklist");
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["deal", dealId],
    queryFn: () => api.deal(dealId!),
    enabled: Boolean(dealId),
  });

  const [stage, setStage] = useState<DealStage | "">("");
  const [reason, setReason] = useState("");

  const move = useMutation({
    mutationFn: () =>
      api.patchDeal(dealId!, {
        stage: stage || undefined,
        terminated_reason: reason || null,
      }),
    onSuccess: (d) => {
      toast.push(`Deal moved to ${DEAL_STAGE_LABEL[d.deal.stage]}`);
      setStage("");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  if (isLoading) return <Spinner label="Loading deal…" />;
  if (error) return <div className="p-4"><ErrorNote error={error} /></div>;
  if (!data) return null;

  const d = data.deal;
  const byCategory = new Map<string, ChecklistItemRow[]>();
  for (const item of data.checklist) {
    const list = byCategory.get(item.category) ?? [];
    list.push(item);
    byCategory.set(item.category, list);
  }

  return (
    <div className="p-4">
      <header className="mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/deals" className="text-xs text-fg-muted hover:underline">
            Deals
          </Link>
          <span className="text-xs text-fg-muted">/</span>
          <h1 className="text-lg font-semibold tracking-tight" data-testid="deal-name">
            {d.name}
          </h1>
          <Badge tone={dealStageTone(d.stage)}>{DEAL_STAGE_LABEL[d.stage]}</Badge>
          <Badge tone="muted">{STRATEGY_LABEL[d.strategy]}</Badge>
          <Link to={`/leads/${d.lead_id}`} className="text-xs text-fg-muted underline">
            seller: {d.seller_name ?? "—"}
          </Link>
        </div>
        <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-fg-muted">
          <span>
            Contract <b className="tabular-nums text-fg">{formatMoneyCents(d.contract_price_cents)}</b>
          </span>
          <span>
            Close <b className="text-fg">{formatDate(d.closing_date)}</b>
          </span>
          <span>
            p(close){" "}
            <b className="tabular-nums text-fg">
              {d.probability_of_close === null ? "—" : formatPct(Number(d.probability_of_close))}
            </b>
          </span>
          <span>
            Spread{" "}
            <b className="tabular-nums text-fg">{formatMoneyCents(d.projected_spread_cents)}</b>
          </span>
          <span>
            EV <b className="tabular-nums text-fg">{formatMoneyCents(d.expected_value_cents)}</b>
          </span>
          <span>
            {formatAcres(d.parcel_acres)} · {d.parcel_county} {d.parcel_state}
          </span>
        </dl>
      </header>

      {data.kill_checks.length > 0 && (
        <div className="mb-3 space-y-1" data-testid="deal-kill-banners">
          {data.kill_checks.map((k) => (
            <div
              key={k.key}
              role="alert"
              data-testid={k.review_only ? "kill-flag" : "kill-banner"}
              className={cn(
                "rounded border px-3 py-2 text-sm",
                k.review_only
                  ? "border-warn/40 bg-warn/10 text-warn"
                  : "border-danger/40 bg-danger/10 text-danger",
              )}
            >
              <strong className="font-semibold">
                {k.review_only ? "Flag:" : "Kill check:"}
              </strong>{" "}
              {k.label} — {k.detail}
            </div>
          ))}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-end gap-2 rounded border bg-surface p-2">
        <Field label="Stage">
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value as DealStage)}
            className={inputClass + " w-48"}
            data-testid="deal-stage-select"
          >
            <option value="">{DEAL_STAGE_LABEL[d.stage]} (current)</option>
            {data.allowed_stages.map((s) => (
              <option key={s} value={s}>
                {DEAL_STAGE_LABEL[s]}
              </option>
            ))}
          </select>
        </Field>
        {stage === "terminated" && (
          <div className="min-w-56 flex-1">
            <Field label="Why" required>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={inputClass}
                data-testid="deal-terminated-reason"
              />
            </Field>
          </div>
        )}
        <Button
          variant="primary"
          disabled={!stage || move.isPending}
          onClick={() => move.mutate()}
          data-testid="deal-stage-save"
        >
          Move
        </Button>
      </div>

      <div className="mb-2 flex flex-wrap gap-1" role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            data-testid={`deal-tab-${t.toLowerCase()}`}
            className={cn(
              "rounded px-2 py-1 text-xs font-medium",
              tab === t ? "bg-brand/15 text-brand" : "text-fg-muted hover:bg-surface-2",
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Checklist" && (
        <Card
          title={`Due diligence (${data.checklist.length - d.checklist_todo}/${data.checklist.length} done)`}
          data-testid="deal-checklist"
        >
          <DiligenceFindings deal={data} />
          {[...byCategory.entries()].map(([category, items]) => (
            <div key={category} className="mb-3 last:mb-0">
              <h3 className="mb-1 text-[11px] font-semibold tracking-wide uppercase text-fg-muted">
                {CHECKLIST_CATEGORY_LABEL[category] ?? category}
              </h3>
              <table className="w-full">
                <tbody>
                  {items.map((item) => (
                    <ChecklistRow key={item.id} item={item} />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Card>
      )}

      {tab === "Engagements" && (
        <Card title={`Engagements (${data.engagements.length})`} data-testid="deal-engagements">
          <AddEngagement deal={data} />
          {data.engagements.length === 0 ? (
            <Empty title="No vendor work yet." hint="Add an engagement to draft an RFQ." />
          ) : (
            <div className="space-y-2">
              {data.engagements.map((e) => (
                <EngagementCard key={e.id} engagement={e} />
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "Funding" && (
        <Card title={`Funding (${data.funding.length})`}>
          {data.funding.length === 0 ? (
            <Empty title="No funding recorded." />
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-fg-muted">
                  <th className="py-1">Investor</th>
                  <th className="py-1 text-right">Amount</th>
                  <th className="py-1">Structure</th>
                  <th className="py-1">Terms</th>
                  <th className="py-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.funding.map((f) => (
                  <tr key={f.id} className="border-b last:border-b-0">
                    <td className="py-1">{f.investor_name}</td>
                    <td className="py-1 text-right tabular-nums">
                      {formatMoneyCents(f.amount_cents)}
                    </td>
                    <td className="py-1">{humanise(f.structure)}</td>
                    <td className="py-1">{f.rate_or_split ?? "—"}</td>
                    <td className="py-1">
                      <Badge tone={f.status === "funded" ? "ok" : "muted"}>
                        {humanise(f.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="mt-2 text-[11px] text-fg-muted">
            Committed {formatMoneyCents(d.funding_committed_cents)} against a contract price of{" "}
            {formatMoneyCents(d.contract_price_cents)}.
          </p>
        </Card>
      )}

      {tab === "Underwriting" && <UnderwritingTab deal={data} />}

      {tab === "Disposition" && (
        <Card title={`Disposition (${data.dispositions.length})`}>
          {data.dispositions.length === 0 ? (
            <Empty title="Nothing listed yet." />
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-fg-muted">
                  <th className="py-1">Buyer</th>
                  <th className="py-1">Channel</th>
                  <th className="py-1 text-right">List</th>
                  <th className="py-1 text-right">Offer</th>
                  <th className="py-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.dispositions.map((x) => (
                  <tr key={x.id} className="border-b last:border-b-0">
                    <td className="py-1">{x.buyer_name ?? "—"}</td>
                    <td className="py-1">{humanise(x.channel)}</td>
                    <td className="py-1 text-right tabular-nums">
                      {formatMoneyCents(x.list_price_cents)}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {formatMoneyCents(x.offer_cents)}
                    </td>
                    <td className="py-1">{humanise(x.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "Documents" && (
        <Card
          title={`Documents (${data.documents.length})`}
          action={<FileUpload entityType="deal" entityId={d.id} kind="other" testId="upload-deal-doc" />}
          data-testid="deal-documents"
        >
          {data.documents.length === 0 ? (
            <Empty title="No documents." hint="Uploads are private; links are signed and short-lived." />
          ) : (
            <ul className="space-y-1 text-xs">
              {data.documents.map((doc) => (
                <li key={doc.id} className="flex items-center gap-2 rounded border px-2 py-1.5">
                  <Badge tone="muted">{humanise(doc.kind)}</Badge>
                  <DownloadLink documentId={doc.id} filename={doc.filename} />
                  <span className="ml-auto tabular-nums text-fg-muted">
                    {doc.size_bytes ? `${Math.round(doc.size_bytes / 1024)} kB` : "—"}
                  </span>
                  <span className="text-fg-muted">{formatDate(doc.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {tab === "Timeline" && (
        <Card title={`Timeline (${data.events.length})`}>
          <ul className="space-y-1" data-testid="deal-timeline">
            {data.events.map((e) => (
              <li key={e.id} className="rounded border px-2 py-1.5 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={e.event_type === "stage_changed" ? "info" : "muted"}>
                    {e.event_type}
                  </Badge>
                  <span className="text-fg-muted">{e.entity_type}</span>
                  <span className="text-fg-muted">{e.actor}</span>
                  <span className="ml-auto tabular-nums text-fg-muted">
                    {formatDateTime(e.occurred_at)}
                  </span>
                </div>
                {(e.from_value ?? e.to_value) && (
                  <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words text-[11px] text-fg-muted">
                    {JSON.stringify(e.from_value)} → {JSON.stringify(e.to_value)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
